import queryString from 'query-string';
import React, { useCallback, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import styled, { StyledComponentPropsWithRef } from 'styled-components';
import { useAppSelector } from '~/javascripts/admin/hooks/useApp';
import { selectUserPrivateMoney } from '~/javascripts/admin/reducers/user-private-money';
import Headings from '../../atoms/Headings';
import UserPrivateMoneyNotAllowed from '../../atoms/UserPrivateMoneyNotAllowed';
import ApiResults from '../../molecules/ApiResults';
import BreadCrumb from '../../molecules/BreadCrumb';
import ButtonGroup from '../../molecules/ButtonGroup';
import SelectedPrivateMoneyDisplay from '../../organisms/SelectedPrivateMoneyDisplay';
import CampaignsTable from './CampaignsTable';
import MoneyAccountHooksTable from './MoneyAccountHooksTable';

const PageWrapper = styled.div`
  ${ButtonGroup} {
    margin-bottom: 20px;
  }
`;

const Header = styled(Headings.h5)`
  margin-bottom: 14px;
`;

const CRUMBS = [
  { value: 'ホーム', href: '/', isCurrentPage: false },
  { value: '販促管理', href: '', isCurrentPage: true },
  { value: 'キャンペーン管理', href: '', isCurrentPage: true },
];

const TABS: StyledComponentPropsWithRef<typeof ButtonGroup>['buttons'] = [
  { text: '適用中', value: 'ongoing' },
  { text: '適用外', value: 'stopped' },
  { text: 'ウォレット追加時ポイント還元', value: 'money-account-hook' },
];

const Campaigns = () => {
  const history = useHistory();
  const location = useLocation();
  const params = queryString.parse(location.search);

  const getActiveTabIndex = () => {
    const tab = params?.tab;
    return typeof tab === 'string' ? Number(tab) : 0;
  };

  const activeTabIndex = getActiveTabIndex();

  const [apiMessages, setApiMessages] =
    useState<StyledComponentPropsWithRef<typeof ApiResults>['messages']>(undefined);

  const user = useAppSelector((state) => state.user);
  const userPrivateMoney = useAppSelector(selectUserPrivateMoney);
  const allowedPrivateMoneys = user.isServiceUser() ? user.privateMoneys : user.issuedMoneys();
  const isSelectedMoneyAllowed = allowedPrivateMoneys?.some((m) => m.id === userPrivateMoney.id);

  const handleSelectTab = useCallback(
    (index: number) => {
      history.push(`/promotion/campaigns?tab=${index}`);
    },
    [history]
  );

  return (
    <PageWrapper>
      <BreadCrumb crumbs={CRUMBS} />
      <Header>キャンペーン一覧</Header>
      <SelectedPrivateMoneyDisplay
        highlightHeader={!isSelectedMoneyAllowed}
        allowedPrivateMoneys={allowedPrivateMoneys}
        wrapperStyles={{ marginBottom: 16 }}
      />
      {isSelectedMoneyAllowed ? (
        <>
          <ButtonGroup
            buttons={TABS}
            activeIndex={activeTabIndex}
            onClick={(_, index) => handleSelectTab(index)}
          />
          {
            [
              <CampaignsTable
                privateMoneyId={userPrivateMoney.id}
                setApiResult={setApiMessages}
                isOngoing
              />,
              <CampaignsTable privateMoneyId={userPrivateMoney.id} setApiResult={setApiMessages} />,
              <MoneyAccountHooksTable
                privateMoneyId={userPrivateMoney.id}
                setApiResult={setApiMessages}
              />,
            ][activeTabIndex]
          }
          <ApiResults messages={apiMessages} />
        </>
      ) : (
        <UserPrivateMoneyNotAllowed />
      )}
    </PageWrapper>
  );
};

export default Campaigns;
